/**
 * Écran **Historique** `/historique` (DECISIONS §4.2, `GET /me/runs`).
 *
 * Le serveur ne renvoie que les runs du **joueur du jeton** : aucun `userId` n’est transmis par
 * le client et aucun n’est affiché. Les valeurs montrées (statut, niveau atteint, éclats
 * validés) sont recopiées telles quelles ; le client ne recompte ni ne corrige rien
 * (DECISIONS §8).
 *
 * Pagination : le contrat n’expose qu’une borne `limit` (1 à 50), sans curseur. L’écran charge
 * donc 20 parties, puis 30, puis 50 au maximum — jamais au-delà de ce que le serveur accepte.
 * Les dates passent par `Intl.DateTimeFormat` (DECISIONS §9.3) : aucun format écrit à la main.
 */
import { RUNS_PAGE_LIMIT_DEFAUT, RUNS_PAGE_LIMIT_MAX } from '@elemora/shared/constants';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { lireHistorique, useSessionStore, versErreurApi, type ErreurApi } from '../api';
import { formaterDate, formaterNombre } from '../i18n';
import { BandeauErreur } from '../ui/BandeauErreur';
import { Bouton } from '../ui/Bouton';
import { Chargement } from '../ui/Chargement';
import { Entete } from '../ui/Entete';

import type { RunResume } from '@elemora/shared/api';

/** Pas d’augmentation de la fenêtre d’historique (bornée par le contrat). */
const PAS_PAGINATION = 10;

export function EcranHistorique() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const progression = useSessionStore((etat) => etat.progression);

  const [limite, setLimite] = useState<number>(RUNS_PAGE_LIMIT_DEFAUT);
  const [runs, setRuns] = useState<readonly RunResume[] | null>(null);
  const [erreur, setErreur] = useState<ErreurApi | null>(null);
  const [tentative, setTentative] = useState(0);

  /** Chargement partagé : un seul aller-retour même si React exécute l’effet deux fois. */
  const chargementRef = useRef<{ cle: string; promesse: Promise<readonly RunResume[]> } | null>(null);

  function charger(cle: string): Promise<readonly RunResume[]> {
    const existant = chargementRef.current;
    if (existant !== null && existant.cle === cle) return existant.promesse;
    const promesse = lireHistorique(limite).then((reponse) => reponse.runs);
    chargementRef.current = { cle, promesse };
    return promesse;
  }

  useEffect(() => {
    let actif = true;
    charger(`${limite}:${tentative}`)
      .then((resultat) => {
        if (!actif) return;
        setRuns(resultat);
        setErreur(null);
      })
      .catch((erreurApi: unknown) => {
        if (!actif) return;
        setErreur(versErreurApi(erreurApi));
      });
    return () => {
      actif = false;
    };
  }, [limite, tentative]);

  const borneAtteinte = limite >= RUNS_PAGE_LIMIT_MAX;
  const peutAfficherPlus = runs !== null && runs.length >= limite && !borneAtteinte;

  return (
    <div className="ecran">
      <Entete avecDeconnexion />
      <main className="page-liste">
        <header className="page-liste__entete">
          <h1 className="page-liste__titre">{t('historique.titre')}</h1>
          <p className="page-liste__sousTitre">{t('historique.sousTitre')}</p>
        </header>

        {progression === null ? null : (
          <p className="note" data-niveau-max={progression.niveauMax}>
            {t('historique.niveauMaximum', {
              niveau: formaterNombre(progression.niveauMax, i18n.language),
            })}
          </p>
        )}

        {runs === null ? (
          erreur === null ? (
            <Chargement />
          ) : (
            <BandeauErreur
              erreur={erreur}
              surReessai={() => {
                setTentative((valeur) => valeur + 1);
              }}
            />
          )
        ) : erreur !== null ? (
          <BandeauErreur
            erreur={erreur}
            surReessai={() => {
              setTentative((valeur) => valeur + 1);
            }}
          />
        ) : runs.length === 0 ? (
          <section className="panneau panneau--vide">
            <h2 className="panneau__titre">{t('historique.vide')}</h2>
            <p className="note">{t('historique.videAide')}</p>
          </section>
        ) : (
          <>
            <ul className="historique" aria-label={t('historique.liste')}>
              {runs.map((run) => (
                <li className="panneau historique__partie" key={run.id} data-run={run.id}>
                  <div className="historique__entete">
                    <h2 className="historique__date">{formaterDate(run.startedAt, i18n.language)}</h2>
                    <p className={`etiquette etiquette--${run.status}`} data-statut={run.status}>
                      {t(`historique.statutValeur.${run.status}`)}
                    </p>
                  </div>
                  <dl className="historique__stats">
                    <div className="historique__stat">
                      <dt className="historique__etiquette">{t('historique.mode')}</dt>
                      <dd className="historique__valeur">{t(`historique.modes.${run.mode}`)}</dd>
                    </div>
                    <div className="historique__stat">
                      <dt className="historique__etiquette">{t('historique.niveauAtteint')}</dt>
                      <dd className="historique__valeur" data-niveau-atteint={run.niveauAtteint}>
                        {t('historique.niveauValeur', {
                          niveau: formaterNombre(run.niveauAtteint, i18n.language),
                        })}
                      </dd>
                    </div>
                    <div className="historique__stat">
                      <dt className="historique__etiquette">{t('historique.eclatsValides')}</dt>
                      <dd className="historique__valeur" data-eclats-valides={run.eclatsValides}>
                        {t('historique.eclats', {
                          count: run.eclatsValides,
                          valeur: formaterNombre(run.eclatsValides, i18n.language),
                        })}
                      </dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>

            <div className="historique__pagination">
              {peutAfficherPlus ? (
                <Bouton
                  variante="secondaire"
                  onClick={() => {
                    setLimite((valeur) =>
                      Math.min(valeur + PAS_PAGINATION, RUNS_PAGE_LIMIT_MAX),
                    );
                  }}
                >
                  {t('historique.afficherPlus')}
                </Bouton>
              ) : null}
              {borneAtteinte ? (
                <p className="note" data-borne-historique={RUNS_PAGE_LIMIT_MAX}>
                  {t('historique.borne', { max: formaterNombre(RUNS_PAGE_LIMIT_MAX, i18n.language) })}
                </p>
              ) : null}
            </div>
          </>
        )}

        <nav className="page-liste__pied" aria-label={t('commun.navigation')}>
          <Bouton variante="secondaire" onClick={() => navigate('/')}>
            {t('commun.retourAccueil')}
          </Bouton>
          <Bouton variante="discret" onClick={() => navigate('/ameliorations')}>
            {t('historique.versAmeliorations')}
          </Bouton>
        </nav>
      </main>
    </div>
  );
}
